import PropTypes from "prop-types";
import { Input, InputNumber, Select } from "top-component-library";
import { FormRow } from "../styles";

const QUANTITY_OPTIONS = Array.from({ length: 10 }, (_, index) => ({ value: index + 1, label: String(index + 1) }));
const UNLOCKED_QUANTITY_OPTIONS = [{ value: 0, label: "unlimited" }, ...QUANTITY_OPTIONS];
const SAME_ORDER_OR_PAST_OPTIONS = [
  { value: "same_order", label: "same order" },
  { value: "past", label: "past" },
];

const displayNameFor = (availableThrottleSettings, tagName) =>
  availableThrottleSettings.find((tag) => tag.name === tagName)?.display_name || tagName;

// Mirrors add-order-rate-limit-dialog.html's inputPrereq step: "To place an order containing
// [unlockedTag], the guest must have ordered [prereqTag] in the [same order|past N minutes]."
// The quantity mapping (purchasing N prereqTag items enables M unlockedTag items) only shows
// once both tags are chosen.
const OrderRateLimitPrereqStep = ({
  availableThrottleSettings,
  unlockedTag,
  prereqTag,
  showPrereqMinutes,
  prereqTagMinutesInPast,
  prereqTagMinQty,
  unlockedTagMaxQty,
  messageIfViolated,
  onChangeUnlockedTag,
  onChangePrereqTag,
  onChangeShowPrereqMinutes,
  onChangePrereqTagMinutesInPast,
  onChangePrereqTagMinQty,
  onChangeUnlockedTagMaxQty,
  onChangeMessage,
}) => {
  const tagOptions = availableThrottleSettings.map((tag) => ({ value: tag.name, label: tag.display_name }));

  return (
    <div>
      <FormRow>
        <label htmlFor="unlocked-tag">To place an order containing</label>
        <Select
          id="unlocked-tag"
          options={tagOptions}
          value={unlockedTag || undefined}
          onChange={onChangeUnlockedTag}
        />
      </FormRow>
      <FormRow>
        <label htmlFor="prereq-tag">the guest must have ordered</label>
        <Select
          id="prereq-tag"
          options={tagOptions.map((option) => ({ ...option, disabled: option.value === unlockedTag }))}
          value={prereqTag || undefined}
          onChange={onChangePrereqTag}
        />
      </FormRow>
      <FormRow>
        <label htmlFor="prereq-when">in the</label>
        <Select
          id="prereq-when"
          options={SAME_ORDER_OR_PAST_OPTIONS}
          value={showPrereqMinutes ? "past" : "same_order"}
          onChange={(value) => onChangeShowPrereqMinutes(value === "past")}
        />
        {showPrereqMinutes && (
          <>
            <InputNumber
              aria-label="Minutes in the past"
              value={prereqTagMinutesInPast}
              onChange={onChangePrereqTagMinutesInPast}
            />
            <span>minutes.</span>
          </>
        )}
      </FormRow>
      {prereqTag && unlockedTag && (
        <>
          <FormRow>
            <label htmlFor="prereq-tag-min-qty">
              Purchasing this many {displayNameFor(availableThrottleSettings, prereqTag)} items
            </label>
            <Select
              id="prereq-tag-min-qty"
              options={QUANTITY_OPTIONS}
              value={prereqTagMinQty}
              onChange={onChangePrereqTagMinQty}
            />
          </FormRow>
          <FormRow>
            <label htmlFor="unlocked-tag-max-qty">
              enables purchase of this many {displayNameFor(availableThrottleSettings, unlockedTag)} menu items
            </label>
            <Select
              id="unlocked-tag-max-qty"
              options={UNLOCKED_QUANTITY_OPTIONS}
              value={unlockedTagMaxQty ?? 0}
              onChange={onChangeUnlockedTagMaxQty}
            />
          </FormRow>
          <h4>Error Message:</h4>
          <FormRow>
            <label htmlFor="message-if-violated-prereq">Message shown to patrons when their order is blocked</label>
            <Input
              id="message-if-violated-prereq"
              maxLength={250}
              value={messageIfViolated}
              onChange={(event) => onChangeMessage(event.target.value)}
            />
          </FormRow>
        </>
      )}
    </div>
  );
};

OrderRateLimitPrereqStep.propTypes = {
  availableThrottleSettings: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, display_name: PropTypes.string.isRequired })
  ).isRequired,
  unlockedTag: PropTypes.string.isRequired,
  prereqTag: PropTypes.string.isRequired,
  showPrereqMinutes: PropTypes.bool.isRequired,
  prereqTagMinutesInPast: PropTypes.number.isRequired,
  prereqTagMinQty: PropTypes.number.isRequired,
  unlockedTagMaxQty: PropTypes.number,
  messageIfViolated: PropTypes.string.isRequired,
  onChangeUnlockedTag: PropTypes.func.isRequired,
  onChangePrereqTag: PropTypes.func.isRequired,
  onChangeShowPrereqMinutes: PropTypes.func.isRequired,
  onChangePrereqTagMinutesInPast: PropTypes.func.isRequired,
  onChangePrereqTagMinQty: PropTypes.func.isRequired,
  onChangeUnlockedTagMaxQty: PropTypes.func.isRequired,
  onChangeMessage: PropTypes.func.isRequired,
};

OrderRateLimitPrereqStep.defaultProps = {
  unlockedTagMaxQty: null,
};

export default OrderRateLimitPrereqStep;
